import moment from 'moment';
import { formatNumber } from 'utils/number';
import type { ChartSeriesEntry, ChartSeriesResponse } from './types';

/**
 * Reshapes a `chart_series` response (the uniform `series[]` contract) into the point
 * arrays each SciChart body consumes. Net-new (INS-9994) — the seam between the shared
 * data endpoint and the SciChart renderers.
 */

/** One labelled bar/line category and its aggregated value. */
export interface LabelledValue {
  label: string;
  value: number;
}

/** A single scatter point. */
interface ScatterPoint {
  x: number;
  y: number;
}

/**
 * Bar/line entry → its `[{ label, value }]` array (already aggregated by the BE). Empty
 * array when the entry is absent (empty result) so callers render an empty chart, not a
 * crash.
 *
 * @param {ChartSeriesEntry | undefined} entry - A bar/line series entry.
 * @return {LabelledValue[]} The labelled values (empty when absent).
 */
export function toBarValues(
  entry: ChartSeriesEntry | undefined
): LabelledValue[] {
  return entry?.values ?? [];
}

/**
 * Scatter entry → its `[{ x, y }]` points (already sampled server-side when large).
 *
 * @param {ChartSeriesEntry | undefined} entry - A scatter series entry.
 * @return {ScatterPoint[]} The points (empty when absent).
 */
export function toScatterPoints(
  entry: ChartSeriesEntry | undefined
): ScatterPoint[] {
  return entry?.points ?? [];
}

/**
 * Line entry whose X labels are `month` strings (`YYYY-MM`, the BE's truncated cashflow
 * date) → `[timestampMs, value][]` sorted ascending — the tuple shape SciChart's
 * `XyDataSeries` / the production chart consume. Rows whose label doesn't parse as a
 * month are dropped (defensive; the BE always sends `YYYY-MM`).
 *
 * @param {ChartSeriesEntry | undefined} entry - A line (temporal) series entry.
 * @return {Array<[number, number]>} `[timestampMs, value]` tuples, ascending by time.
 */
export function toLineSeries(
  entry: ChartSeriesEntry | undefined
): [number, number][] {
  return (entry?.values ?? [])
    .map(({ label, value }): [number, number] => [
      moment(label, 'YYYY-MM', true).valueOf(),
      value,
    ])
    .filter(([ts]) => Number.isFinite(ts))
    .sort((a, b) => a[0] - b[0]);
}

/**
 * The "showing a sample of N" note for a down-sampled scatter, or `null` when the full
 * population was returned. Reads `meta.sampled` / `meta.sampleCap` (present only when
 * sampled) so the body can surface the cap without recomputing it.
 *
 * @param {ChartSeriesResponse.meta | undefined} meta - The response meta.
 * @return {string | null} The sample note, or null when not sampled.
 */
export function sampledNote(
  meta: ChartSeriesResponse['meta'] | undefined
): string | null {
  if (!meta?.sampled) {
    return null;
  }
  const cap = meta.sampleCap;
  const of = meta.slotCount ? ` of ${formatNumber(meta.slotCount, 0)}` : '';
  return cap
    ? `Showing a representative sample of ${formatNumber(cap, 0)}${of} points.`
    : `Showing a representative sample${of}.`;
}
